// @flow
import { PDFFillerContext } from 'pages/Contexts/PdfFillerContext'
import * as React from 'react'
import { toast } from 'react-toastify'
import styled from 'styled-components'
import tw from 'tailwind-styled-components'
import type { AnswerSignature } from 'types/answer_signature'
import type { InputField } from 'types/input-field'
import { SignatureIcon } from 'ui/Icons'
import SignatureQuestionRender from 'ui/SignatureQuestionRender'

import { useSaveAnswerSignature } from '@/queries/answers/answerSignature/useSaveAnswerSignature'
import Modal from '@/ui/Modal'

type Answer = {|
  image: ?string,
  text: ?string,
  untrimmedImage?: ?string,
  id?: any,
|}

const SignatureContainer = styled.div`
  width: 100%;
  height: 100%;
  cursor: pointer;
  position: relative;
`

const InitialsPlaceholder = styled.span.attrs({
  children: 'XX',
})`
  font-size: ${({ $fontSize }: { $fontSize: number }) => $fontSize}px;
  text-decoration: underline;
`

const StyledInput = tw.div`
  h-full
  flex
  flex-col
  justify-center
  items-start

  ${({ $pointer }: { $pointer: boolean }) =>
    $pointer ? 'cursor-pointer' : null}
`

const StyledSignatureImage = tw.img`
  h-full
`

const StyledSignatureText = styled.p`
  font-family: cursive;
`

const PlaceholderWrapper = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
`

type Props = {
  inputField: InputField,
  initials?: boolean,
  fontSize: number,
}

function SignatureInput({ inputField, initials, fontSize }: Props): React.Node {
  const {
    resourceId,
    setErrors,
    setSignatureAnswers,
    signatureAnswers,
    viewMode,
    signatureMemo,
    inputFields,
  } = React.useContext(PDFFillerContext)
  const { saveAnswerSignature } = useSaveAnswerSignature()

  const [modalVisible, setModalVisible] = React.useState(false)

  const answer = React.useMemo(() => {
    return signatureAnswers[inputField.id] || null
  }, [signatureAnswers, inputField.id])

  const createSignatureAnswer = React.useCallback(
    async (answer: Answer, inputFieldId: string, resourceId: string) => {
      const signature = await saveAnswerSignature({
        responseId: resourceId,
        imageFile: answer.image,
        text: answer.text,
        mode: viewMode,
        inputFieldId,
      })

      const newSignatureAnswer: AnswerSignature = {
        ...answer,
        input_field: inputFieldId,
        response: resourceId,
        ip_address: signature?.ip_address ?? '',
        created_at: signature?.created_at ?? '',
      }

      return newSignatureAnswer
    },
    [saveAnswerSignature, viewMode]
  )

  const handleHide = React.useCallback(() => {
    setModalVisible(false)
  }, [setModalVisible])

  const memorizeAnswer = React.useCallback(
    (answer) => {
      if (!inputField.client_signature) return

      if (initials) {
        signatureMemo?.setMemorizedInitials(answer)
        return
      }

      signatureMemo?.setMemorizedSignature(answer)
    },
    [initials, inputField.client_signature, signatureMemo]
  )

  const memorizedAnswer = React.useMemo(() => {
    if (viewMode !== 'office-use' && !inputField.client_signature) return

    if (initials) {
      return signatureMemo?.memorizedInitials
    }

    return signatureMemo?.memorizedSignature
  }, [
    inputField?.client_signature,
    viewMode,
    initials,
    signatureMemo?.memorizedInitials,
    signatureMemo?.memorizedSignature,
  ])

  const handleSignatureConfirmation = React.useCallback(
    async (answer: Answer) => {
      try {
        const newSignatureAnswer = await createSignatureAnswer(
          answer,
          inputField.id,
          resourceId
        )

        setSignatureAnswers((prevSignatureAnswers) => {
          return {
            ...prevSignatureAnswers,
            [inputField.id]: {
              ...newSignatureAnswer,
            },
          }
        })

        setErrors((prevErrors) => {
          return {
            ...prevErrors,
            [inputField.id]: '',
          }
        })

        memorizeAnswer(answer)

        handleHide()
      } catch {
        toast.error('Signature could not be saved')
      }
    },
    [
      createSignatureAnswer,
      inputField.id,
      resourceId,
      setSignatureAnswers,
      setErrors,
      memorizeAnswer,
      handleHide,
    ]
  )

  const handleClick = React.useCallback(async () => {
    if (!['fill', 'preview', 'office-use'].includes(viewMode)) return

    if (viewMode === 'office-use' && !inputField.is_office_use) return

    if (viewMode !== 'office-use' && inputField.is_office_use) return

    if (!answer && memorizedAnswer) {
      if (viewMode === 'office-use' || inputField.client_signature) {
        await handleSignatureConfirmation(memorizedAnswer)
        return
      }
    }

    if (!modalVisible) setModalVisible(true)
  }, [
    modalVisible,
    answer,
    handleSignatureConfirmation,
    inputField.is_office_use,
    inputField.client_signature,
    memorizedAnswer,
    viewMode,
  ])

  const handleResetSignature = React.useCallback(() => {
    setSignatureAnswers((prevSignatureAnswers) => {
      return {
        ...prevSignatureAnswers,
        [inputField.id]: {
          id: prevSignatureAnswers[inputField.id]?.id,
        },
      }
    })
  }, [inputField.id, setSignatureAnswers])

  const renderInputContent = React.useCallback(() => {
    if (answer?.image) {
      return <StyledSignatureImage alt="signature" src={answer.image} />
    }

    if (answer?.text) {
      return <StyledSignatureText>{answer.text}</StyledSignatureText>
    }

    if (viewMode === 'response') return null

    return null
  }, [answer, viewMode])

  const renderPlaceholder = React.useCallback(() => {
    if (answer?.image || answer?.text) return null
    if (viewMode === 'response') return null

    return (
      <PlaceholderWrapper>
        {initials ? (
          <InitialsPlaceholder $fontSize={fontSize} />
        ) : (
          <SignatureIcon height={fontSize} width={fontSize} />
        )}
      </PlaceholderWrapper>
    )
  }, [answer?.image, answer?.text, fontSize, initials, viewMode])

  const onInitialAll = React.useCallback(
    async (answer) => {
      if (!initials) return

      const inputFieldsArray = Object.values(inputFields)

      const blankInitialsInputs = inputFieldsArray.filter(
        (inputField) =>
          inputField.input_type === 'initials' &&
          !signatureAnswers[inputField.id]
      )

      const newAnswers: { [string]: any } = {}
      const newErrors: { [string]: string } = {}

      const signatureAnswer = await createSignatureAnswer(
        answer,
        '',
        resourceId
      )

      for (const initialsInput of blankInitialsInputs) {
        newAnswers[initialsInput.id] = {
          ...signatureAnswer,
          input_field: initialsInput.id,
        }

        newErrors[initialsInput.id] = ''
      }

      setSignatureAnswers((signatureAnswers) => ({
        ...signatureAnswers,
        ...newAnswers,
      }))

      setErrors((errors) => ({
        ...errors,
        ...newErrors,
      }))

      memorizeAnswer(answer)

      handleHide()
    },
    [
      createSignatureAnswer,
      handleHide,
      initials,
      inputFields,
      memorizeAnswer,
      resourceId,
      setErrors,
      setSignatureAnswers,
      signatureAnswers,
    ]
  )

  return (
    <SignatureContainer onClick={handleClick}>
      {renderPlaceholder()}
      <StyledInput $pointer={viewMode === 'fill' || viewMode === 'preview'}>
        {renderInputContent()}
      </StyledInput>
      <Modal
        destroyOnClose
        classNames={{
          content: 'max-md:h-[96svh]',
          wrapper: 'max-md:[&_.ant-modal]:top-0',
        }}
        footer={() => undefined}
        maskClosable={false}
        onCancel={handleHide}
        open={modalVisible}
        width={{
          xs: '100vw',
          sm: '100vw',
          md: '70%',
          lg: '60%',
          xl: '50%',
          xxl: '40%',
        }}
      >
        <SignatureQuestionRender
          noModal
          answer={answer || memorizedAnswer || null}
          initials={initials}
          isVisible={modalVisible}
          onCancel={handleHide}
          onConfirm={handleSignatureConfirmation}
          onInitialAll={onInitialAll}
          onReset={handleResetSignature}
          subtype="all"
        />
      </Modal>
    </SignatureContainer>
  )
}

export default SignatureInput
